import type React from 'react';
import { useMemo } from 'react';
import type { GroupBase, Props } from 'react-select';
import ReactSelect from 'react-select';
import {
  baseSelectStyles,
  brandedSelectTheme,
  checkboxClass,
  checkboxInvalidClass,
  inputClass,
  inputInvalidClass,
} from './form-styles';

type ReactLabel = React.FC<React.LabelHTMLAttributes<HTMLLabelElement>>;

const Label: ReactLabel = (properties) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: false positive
  <label className="block text-sm font-medium leading-6 text-gray-900" {...properties} />
);

type ReactH2 = React.FC<React.LabelHTMLAttributes<HTMLElement>>;

export const Section: ReactH2 = (properties) => (
  <h2 className="block text-sm font-medium leading-6 text-gray-500" {...properties} />
);

interface FieldProperties
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  label: string;
  errorMessage?: string;
  name: string;
}

export const Field: React.FC<FieldProperties> = ({ label, children, errorMessage, name }) => (
  <div className="grid grid-flow-row">
    <Label htmlFor={name} className="mb-1 block text-sm font-medium leading-6 text-gray-900">
      {label}
    </Label>
    {children}
    {errorMessage && <div className="text-red-500">{errorMessage}</div>}
  </div>
);

type ContainerProperties = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export const Row: React.FC<ContainerProperties> = ({ children }) => (
  <div className="grid grid-flow-col justify-start gap-2">{children}</div>
);

export const FieldDescription: React.FC<ContainerProperties> = ({ children }) => (
  <p className="text-gray-400">{children}</p>
);

interface InputProperties extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  className?: string;
}

export const Input: React.FC<InputProperties> = ({ invalid, className, ...rest }) => {
  const fullClassName = useMemo(
    () => `${invalid ? inputInvalidClass : inputClass} ${className}`,
    [invalid, className]
  );

  return <input className={fullClassName} {...rest} />;
};

export const Checkbox: React.FC<InputProperties> = ({ invalid, className, ...rest }) => {
  const fullClassName = useMemo(
    () => `${invalid ? checkboxInvalidClass : checkboxClass} ${className}`,
    [invalid, className]
  );

  return <input type="checkbox" className={fullClassName} {...rest} />;
};

export type SelectOption<T extends string | number> = {
  value: T;
  label: React.ReactNode;
};

type SelectProperties<T extends string | number> = {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<SelectOption<T>>;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
};

/**
 * Single-value dropdown. Backed by react-select so it shares the same chrome
 * (control, focus, menu, indicators) as MultiSelect — the native `<select>`
 * couldn't be styled to match the menu and shipped two chevrons on most
 * browsers.
 */
export function Select<T extends string | number>({
  value,
  onChange,
  options,
  placeholder,
  isDisabled,
  className,
}: SelectProperties<T>) {
  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <ReactSelect<SelectOption<T>, false>
      value={selected}
      onChange={(option) => {
        if (option) {
          onChange(option.value);
        }
      }}
      options={options as SelectOption<T>[]}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isSearchable={false}
      isClearable={false}
      theme={brandedSelectTheme}
      styles={baseSelectStyles<SelectOption<T>, false>()}
      className={className}
      menuPortalTarget={document.body}
    />
  );
}

export function MultiSelect<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(properties: Props<Option, IsMulti, Group>) {
  return (
    <ReactSelect
      {...properties}
      closeMenuOnSelect={!properties.isMulti}
      menuPortalTarget={document.body}
      theme={brandedSelectTheme}
      styles={baseSelectStyles<Option, IsMulti, Group>()}
      className="min-w-52"
    />
  );
}
